import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Plus, Pencil, Trash2, X, Save, Image as ImageIcon, Tag,
    Calendar, MapPin, User, ChevronUp, ChevronDown, Video, ExternalLink, Star,
} from "lucide-react";
import { toast } from "sonner";
import { api, BASE } from "../../services/api";
import ImageUpload from "../../components/ui/ImageUpload";
import RichTextEditor from "../../components/ui/RichTextEditor";

const CATS = ["koncerty", "firemne", "osvetlenie", "led", "ozvucenie", "svadby", "instalacia"];

const EMPTY = {
    title: "", category: "koncerty", image_url: "",
    images: [], description: "", event_date: "",
    client: "", location: "", tags: [], video_url: "",
    featured: false, featured_order: 99,
};

function resolveImg(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

function parseVideoEmbed(url) {
    if (!url) return null;
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
    const vm = url.match(/vimeo\.com\/(\d+)/);
    if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
    return null;
}

// ── Tags input ─────────────────────────────────────────────────────────────────
function TagsInput({ tags, onChange }) {
    const [input, setInput] = useState("");
    const add = () => {
        const val = input.trim();
        if (val && !tags.includes(val)) onChange([...tags, val]);
        setInput("");
    };
    const remove = (t) => onChange(tags.filter(x => x !== t));
    return (
        <div>
            <div className="flex flex-wrap gap-1.5 mb-2">
                {tags.map(t => (
                    <span key={t} className="flex items-center gap-1 bg-nova-surface border border-white/10 rounded-full px-2.5 py-1 text-xs text-white">
                        {t}
                        <button onClick={() => remove(t)} className="text-zinc-500 hover:text-red-400 transition-colors">
                            <X className="w-2.5 h-2.5" />
                        </button>
                    </span>
                ))}
            </div>
            <div className="flex gap-2">
                <input
                    value={input} onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                    placeholder="Napíšte tag a stlačte Enter…"
                    className="flex-1 h-9 px-3 rounded-lg bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40"
                />
                <button onClick={add} className="px-3 h-9 rounded-lg bg-nova-surface border border-white/10 text-zinc-400 hover:text-white text-sm transition-colors">
                    <Plus className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

// ── Gallery manager with reorder ──────────────────────────────────────────────
function GalleryManager({ images, onChange }) {
    const add  = ()        => onChange([...images, ""]);
    const remove = (i)     => onChange(images.filter((_, idx) => idx !== i));
    const update = (i, v)  => onChange(images.map((img, idx) => idx === i ? v : img));
    const moveUp   = (i)   => { if (i === 0) return; const a = [...images]; [a[i-1], a[i]] = [a[i], a[i-1]]; onChange(a); };
    const moveDown = (i)   => { if (i === images.length - 1) return; const a = [...images]; [a[i], a[i+1]] = [a[i+1], a[i]]; onChange(a); };

    return (
        <div className="space-y-3">
            {images.map((img, i) => (
                <div key={i} className="flex gap-3 items-start p-3 rounded-xl border border-white/8 bg-nova-black/30 group">
                    {/* Thumbnail */}
                    <div className="w-20 h-16 rounded-lg overflow-hidden border border-white/8 bg-nova-black/60 shrink-0">
                        {img ? (
                            <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center">
                                <ImageIcon className="w-4 h-4 text-zinc-700" />
                            </div>
                        )}
                    </div>

                    {/* Upload */}
                    <div className="flex-1 min-w-0">
                        <ImageUpload value={img} onChange={v => update(i, v)} label="" compact />
                        {img && (
                            <p className="mt-1 text-[10px] text-zinc-600 font-mono truncate">{img}</p>
                        )}
                    </div>

                    {/* Controls */}
                    <div className="flex flex-col gap-1 shrink-0">
                        <button onClick={() => moveUp(i)} disabled={i === 0}
                            className="w-7 h-7 rounded flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/8 disabled:opacity-20 transition-colors">
                            <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => moveDown(i)} disabled={i === images.length - 1}
                            className="w-7 h-7 rounded flex items-center justify-center text-zinc-500 hover:text-white hover:bg-white/8 disabled:opacity-20 transition-colors">
                            <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(i)}
                            className="w-7 h-7 rounded flex items-center justify-center text-zinc-500 hover:text-red-400 hover:bg-red-400/8 transition-colors">
                            <Trash2 className="w-3 h-3" />
                        </button>
                    </div>
                </div>
            ))}

            <button onClick={add}
                className="flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-white/15 text-zinc-500 hover:text-white hover:border-white/30 transition-all text-sm w-full justify-center">
                <Plus className="w-3.5 h-3.5" /> Pridať fotku do galérie
            </button>
        </div>
    );
}

// ── Video URL input with preview ──────────────────────────────────────────────
function VideoInput({ value, onChange }) {
    const embed = parseVideoEmbed(value);
    return (
        <div className="space-y-3">
            <div>
                <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 flex items-center gap-1.5 block">
                    <Video className="w-3 h-3" /> YouTube / Vimeo URL
                </label>
                <div className="flex gap-2">
                    <input
                        value={value}
                        onChange={e => onChange(e.target.value)}
                        placeholder="https://www.youtube.com/watch?v=…"
                        className="flex-1 h-10 px-3 rounded-xl bg-nova-black/50 border border-white/8 text-white text-sm outline-none focus:border-nova-cyan/40 font-mono"
                    />
                    {value && (
                        <button onClick={() => onChange("")}
                            className="w-10 h-10 flex items-center justify-center rounded-xl border border-white/8 text-zinc-500 hover:text-red-400 transition-colors">
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
                {value && !embed && (
                    <p className="mt-1.5 text-xs text-amber-400/80">Nepodporovaná URL. Použite YouTube alebo Vimeo.</p>
                )}
            </div>

            {embed && (
                <div className="rounded-xl overflow-hidden border border-white/8 bg-nova-black/40">
                    <div className="aspect-video">
                        <iframe
                            src={embed}
                            title="Video preview"
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                    <div className="px-3 py-2 flex items-center gap-2 border-t border-white/8">
                        <span className="text-[10px] font-mono text-zinc-500 truncate flex-1">{embed}</span>
                        <a href={value} target="_blank" rel="noreferrer"
                            className="text-zinc-500 hover:text-nova-cyan transition-colors">
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Edit panel (slide-out, max-w-3xl) ─────────────────────────────────────────
function EditPanel({ item, onClose, onSave }) {
    const isNew = !item.id;
    const [form, setForm] = useState({ ...EMPTY, ...item });
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState("info");
    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const submit = async () => {
        if (!form.title) { toast.error("Vyplňte názov."); return; }
        setSaving(true);
        try { await onSave(form); }
        catch (e) { toast.error(e.message); setSaving(false); }
    };

    const TABS = [
        { id: "info",   label: "Info",    icon: null },
        { id: "popis",  label: "Popis",   icon: null },
        { id: "media",  label: `Médiá${form.images?.length ? ` (${form.images.length})` : ""}`, icon: null },
        { id: "tags",   label: "Tagy",    icon: null },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-start justify-end"
        >
            <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />
            <motion.div
                initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
                transition={{ type: "spring", damping: 30, stiffness: 300 }}
                className="relative w-full max-w-3xl h-screen bg-[#0d0f14] border-l border-white/8 flex flex-col overflow-hidden shadow-2xl"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/8 shrink-0 bg-nova-surface/20">
                    <div>
                        <div className="font-heading font-black text-white">{isNew ? "Nová referencia" : "Upraviť referenciu"}</div>
                        {!isNew && <div className="font-mono text-[10px] text-zinc-500 mt-0.5 truncate max-w-sm">{item.title}</div>}
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={submit} disabled={saving}
                            className="flex items-center gap-2 px-5 py-2 rounded-lg bg-nova-cyan text-nova-black text-sm font-bold hover:bg-nova-cyan/90 transition-colors disabled:opacity-50">
                            <Save className="w-3.5 h-3.5" />
                            {saving ? "Ukladám…" : "Uložiť"}
                        </button>
                        <button onClick={onClose} className="text-zinc-400 hover:text-white p-1.5 transition-colors rounded-lg hover:bg-white/5">
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-0.5 px-5 pt-3 border-b border-white/8 overflow-x-auto shrink-0">
                    {TABS.map(t => (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className={`px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                                tab === t.id
                                    ? "text-white border-nova-cyan"
                                    : "text-zinc-500 border-transparent hover:text-zinc-300"
                            }`}>
                            {t.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-6 py-6">

                    {/* ── INFO ── */}
                    {tab === "info" && (
                        <div className="space-y-5">
                            <div>
                                <label className="field-label">Názov *</label>
                                <input value={form.title} onChange={e => set("title", e.target.value)}
                                    placeholder="napr. Live koncert — hlavné pódium"
                                    className="field-input font-medium text-base" />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="field-label">Kategória</label>
                                    <select value={form.category} onChange={e => set("category", e.target.value)} className="field-input">
                                        {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="field-label flex items-center gap-1"><Calendar className="w-3 h-3" /> Dátum eventu</label>
                                    <input type="date" value={form.event_date} onChange={e => set("event_date", e.target.value)} className="field-input" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="field-label flex items-center gap-1"><User className="w-3 h-3" /> Klient</label>
                                    <input value={form.client} onChange={e => set("client", e.target.value)}
                                        placeholder="napr. POHODA Festival" className="field-input" />
                                </div>
                                <div>
                                    <label className="field-label flex items-center gap-1"><MapPin className="w-3 h-3" /> Miesto</label>
                                    <input value={form.location} onChange={e => set("location", e.target.value)}
                                        placeholder="napr. Trenčín" className="field-input" />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── POPIS ── */}
                    {tab === "popis" && (
                        <div className="space-y-3">
                            <p className="text-zinc-500 text-xs leading-relaxed">
                                Detailný popis eventu — technické riešenia, zaujímavosti, špeciality projektu.
                                Popis sa zobrazuje na stránke konkrétnej referencie.
                            </p>
                            <RichTextEditor
                                value={form.description}
                                onChange={v => set("description", v)}
                                placeholder="Napíšte podrobný popis eventu, použitej techniky, zaujímavostí…"
                            />
                        </div>
                    )}

                    {/* ── MÉDIÁ ── */}
                    {tab === "media" && (
                        <div className="space-y-8">
                            {/* Cover */}
                            <div>
                                <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-2">
                                    <span className="w-4 h-px bg-zinc-700 inline-block" />
                                    Titulná fotka
                                </h3>
                                <p className="text-zinc-500 text-xs mb-3">
                                    Zobrazuje sa v galérii na hlavnej stránke. Každá fotka je automaticky zmenšená a dostane vodotlač.
                                </p>
                                <ImageUpload value={form.image_url} onChange={v => set("image_url", v)} label="Nahrať titulnú fotku" />
                            </div>

                            {/* Gallery */}
                            <div>
                                <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-2">
                                    <span className="w-4 h-px bg-zinc-700 inline-block" />
                                    Galéria ({form.images?.length || 0} fotiek)
                                </h3>
                                <p className="text-zinc-500 text-xs mb-3">
                                    Fotky galérie sa zobrazia po kliknutí na referenciu. Poradie môžete meniť šípkami.
                                </p>
                                <GalleryManager images={form.images || []} onChange={v => set("images", v)} />
                            </div>

                            {/* Video */}
                            <div>
                                <h3 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 mb-3 flex items-center gap-2">
                                    <span className="w-4 h-px bg-zinc-700 inline-block" />
                                    Video
                                </h3>
                                <VideoInput value={form.video_url || ""} onChange={v => set("video_url", v)} />
                            </div>
                        </div>
                    )}

                    {/* ── TAGY ── */}
                    {tab === "tags" && (
                        <div className="space-y-3">
                            <p className="text-zinc-500 text-xs leading-relaxed">
                                Tagy pomáhajú pri filtrovaní a vyhľadávaní. Napíšte tag a potvrďte Enterom.
                            </p>
                            <TagsInput tags={form.tags || []} onChange={v => set("tags", v)} />

                            {/* Suggested tags */}
                            <div>
                                <p className="font-mono text-[10px] uppercase tracking-wider text-zinc-600 mb-2">Návrhy</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {["festival", "svadba", "konferencia", "outdoor", "indoor", "VIP", "line array", "LED wall", "moving heads", "DJ", "živá hudba"].filter(s => !form.tags?.includes(s)).map(s => (
                                        <button key={s} onClick={() => set("tags", [...(form.tags || []), s])}
                                            className="px-2.5 py-1 rounded-full border border-white/8 text-zinc-500 hover:text-white hover:border-white/20 text-xs transition-colors">
                                            + {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>
    );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminReferencie() {
    const [items, setItems]     = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);

    const load = useCallback(() => {
        api.getReferencie()
            .then(setItems)
            .catch(() => toast.error("Chyba pri načítaní"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (form) => {
        if (form.id) await api.updateReferencia(form.id, form);
        else await api.createReferencia(form);
        setEditing(null);
        toast.success(form.id ? "Referencia aktualizovaná" : "Referencia pridaná");
        load();
    };

    const toggleFeatured = async (item, e) => {
        e.stopPropagation();
        const updated = { ...item, featured: !item.featured };
        await api.updateReferencia(item.id, updated);
        setItems(prev => prev.map(i => i.id === item.id ? updated : i));
    };

    const updateOrder = async (item, val) => {
        const n = parseInt(val);
        if (isNaN(n)) return;
        const updated = { ...item, featured_order: n };
        await api.updateReferencia(item.id, updated);
        setItems(prev => prev.map(i => i.id === item.id ? updated : i));
    };

    const del = async (id) => {
        await api.deleteReferencia(id);
        setDeleting(null);
        toast.success("Referencia odstránená");
        load();
    };

    return (
        <>
            {/* Field helpers (scoped via CSS-in-module-style classes on this page) */}
            <style>{`
                .field-label { display:block; font-family:monospace; font-size:10px; text-transform:uppercase; letter-spacing:0.15em; color:#71717a; margin-bottom:6px; }
                .field-input { width:100%; background:rgba(0,0,0,0.5); border:1px solid rgba(255,255,255,0.08); border-radius:12px; padding:9px 14px; color:#fafafa; font-size:0.875rem; outline:none; height:40px; transition:border-color .15s; }
                .field-input:focus { border-color:rgba(0,153,255,0.4); }
                select.field-input { appearance:auto; }
            `}</style>

            <div className="flex items-center justify-between mb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-1">/ Referencie</div>
                    <h1 className="font-heading text-2xl font-black tracking-tight">Správa referencií</h1>
                    <p className="text-zinc-500 text-xs mt-0.5">
                        {items.length} referencií · Každá fotka je automaticky zmenšená a dostane vodotlač.
                    </p>
                </div>
                <button onClick={() => setEditing({ ...EMPTY })}
                    className="flex items-center gap-2 h-9 px-4 bg-nova-cyan text-black rounded-lg text-sm font-semibold hover:saturate-125 transition-all">
                    <Plus className="w-4 h-4" /> Pridať
                </button>
            </div>

            {loading ? (
                <div className="space-y-2 animate-pulse">
                    {[1,2,3,4,5].map(n => <div key={n} className="h-16 bg-nova-surface/40 rounded-xl" />)}
                </div>
            ) : (
                <div className="border border-white/8 rounded-xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-white/8 bg-nova-surface/30">
                                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 w-14">Foto</th>
                                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Názov</th>
                                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hidden md:table-cell">Kategória</th>
                                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hidden lg:table-cell">Klient / Miesto</th>
                                <th className="text-left px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 hidden sm:table-cell w-20">Médiá</th>
                                <th className="text-center px-4 py-3 font-mono text-[10px] uppercase tracking-wider text-zinc-500 w-24">Hlavná</th>
                                <th className="px-4 py-3 w-20"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, i) => (
                                <tr key={item.id}
                                    className={`border-b border-white/5 hover:bg-white/3 transition-colors cursor-pointer ${i % 2 ? "bg-white/[0.01]" : ""}`}
                                    onClick={() => setEditing(item)}
                                >
                                    <td className="px-4 py-3">
                                        <div className="w-10 h-10 rounded-lg overflow-hidden bg-nova-surface/50 border border-white/6 shrink-0">
                                            {item.image_url
                                                ? <img src={resolveImg(item.image_url)} alt="" className="w-full h-full object-cover" />
                                                : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-3.5 h-3.5 text-zinc-700" /></div>
                                            }
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-white font-medium">{item.title}</td>
                                    <td className="px-4 py-3 hidden md:table-cell">
                                        <span className="font-mono text-[10px] uppercase tracking-wider text-nova-cyan border border-nova-cyan/20 px-2 py-0.5 rounded">{item.category}</span>
                                    </td>
                                    <td className="px-4 py-3 hidden lg:table-cell">
                                        <div className="text-zinc-400 text-xs">{item.client || "—"}</div>
                                        {item.location && <div className="text-zinc-600 text-[10px] font-mono mt-0.5">{item.location}</div>}
                                    </td>
                                    <td className="px-4 py-3 hidden sm:table-cell">
                                        <div className="flex items-center gap-2">
                                            {(item.images?.length > 0) && (
                                                <span className="font-mono text-xs text-zinc-500">+{item.images.length}</span>
                                            )}
                                            {item.video_url && (
                                                <Video className="w-3 h-3 text-nova-cyan/60" />
                                            )}
                                            {!item.images?.length && !item.video_url && (
                                                <span className="text-zinc-700 text-xs">—</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                onClick={e => toggleFeatured(item, e)}
                                                title={item.featured ? "Skryť z hlavnej stránky" : "Zobraziť na hlavnej stránke"}
                                                className={`p-1.5 rounded transition-colors ${item.featured ? "text-yellow-400 hover:text-yellow-300" : "text-zinc-700 hover:text-zinc-400"}`}
                                            >
                                                <Star className="w-4 h-4" fill={item.featured ? "currentColor" : "none"} />
                                            </button>
                                            {item.featured && (
                                                <input
                                                    type="number"
                                                    min={1}
                                                    defaultValue={item.featured_order ?? 99}
                                                    onBlur={e => updateOrder(item, e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                    className="w-12 h-7 px-1.5 text-center rounded bg-nova-black border border-white/10 text-white text-xs focus:outline-none focus:border-nova-cyan/50"
                                                    title="Poradie na hlavnej stránke"
                                                />
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                                        <div className="flex items-center gap-1.5 justify-end">
                                            <button onClick={() => setEditing(item)} className="p-1.5 text-zinc-500 hover:text-white transition-colors">
                                                <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                            <button onClick={() => setDeleting(item.id)} className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {items.length === 0 && (
                                <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-600 text-sm">Žiadne referencie. Pridajte prvú.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <AnimatePresence>
                {editing && (
                    <EditPanel key={editing.id || "new"} item={editing} onClose={() => setEditing(null)} onSave={save} />
                )}
            </AnimatePresence>

            {deleting && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setDeleting(null)} />
                    <div className="relative bg-nova-surface border border-white/10 rounded-xl p-6 max-w-sm w-full z-10">
                        <h2 className="font-heading font-bold text-white mb-2">Odstrániť referenciu?</h2>
                        <p className="text-zinc-400 text-sm mb-5">Táto akcia je nevratná.</p>
                        <div className="flex gap-3">
                            <button onClick={() => setDeleting(null)} className="flex-1 h-10 rounded-lg border border-white/15 text-zinc-300 text-sm hover:bg-white/5 transition-colors">Zrušiť</button>
                            <button onClick={() => del(deleting)} className="flex-1 h-10 rounded-lg bg-red-500 text-white font-semibold text-sm hover:bg-red-600 transition-colors">Odstrániť</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
